import { ReactNode } from 'react'
import { DialogTitle, Stack, Typography } from '@mui/material'
import {
  LockOutlined,
  MailOutline,
  VerifiedUserOutlined,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useAuthContext } from '../AuthContext'
import { ReauthenticationStep } from '../../hooks/use-reauthentication'
import { StatusChip, StepBar } from './ReauthenticationDialog.style'
import { getStepProgress } from './ReauthenticationDialog.utils'

interface ReauthenticationDialogHeaderProps {
  titleId: string
  title: ReactNode
  subtitle?: ReactNode
  step: ReauthenticationStep
  isBusy: boolean
}

const ReauthenticationDialogHeader = ({
  titleId,
  title,
  subtitle,
  step,
  isBusy,
}: ReauthenticationDialogHeaderProps) => {
  const { t } = useTranslation()
  const { user } = useAuthContext()
  const progress = getStepProgress(step, user)

  const getStatus = () => {
    if (isBusy)
      return {
        icon: <LockOutlined />,
        label: t('reauthentication.verifying'),
      }
    if (step === ReauthenticationStep.TwoFactorCode)
      return {
        icon: <VerifiedUserOutlined />,
        label: t('reauthentication.twoFactorTitle'),
      }
    if (step === ReauthenticationStep.EmailLink)
      return {
        icon: <MailOutline />,
        label: t('reauthentication.checkInbox'),
      }
    return {
      icon: <LockOutlined />,
      label: t('reauthentication.required'),
    }
  }

  return (
    <DialogTitle
      component="div"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        px: { xs: 2.25, sm: 3 },
        pt: 3,
        pb: 1,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        <StatusChip size="small" {...getStatus()} />
        {progress.count > 1 && (
          <Stack direction="row" alignItems="center" gap={0.75}>
            {Array.from({ length: progress.count }, (_, index) => (
              <StepBar
                key={index}
                aria-hidden="true"
                isDone={index < progress.current}
              />
            ))}
            <Typography variant="caption" color="grey.600" ml={0.5}>
              {t('reauthentication.step', {
                step: progress.current,
                count: progress.count,
              })}
            </Typography>
          </Stack>
        )}
      </Stack>
      <Typography
        id={titleId}
        variant="h5"
        component="h2"
        sx={{ fontSize: { xs: 20, sm: 24 } }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          fontWeight={700}
          color="grey.600"
          sx={{ fontSize: { xs: 14, sm: 15 } }}
        >
          {subtitle}
        </Typography>
      )}
    </DialogTitle>
  )
}

export default ReauthenticationDialogHeader
